import React, { useState, useCallback, useRef, useEffect } from 'react'
import { useReactFlow } from '@xyflow/react'
import type { Edge } from '@xyflow/react'
import { RoutePoint } from '../utils/edgeRouting'

interface ManualRouteEditorProps {
  selectedEdge: Edge | null
  onRouteUpdate: (connectionId: number, pathPoints: RoutePoint[], isManual: boolean) => void
  onClose: () => void
}

interface RoutePointHandle {
  id: string
  x: number
  y: number
  type: 'waypoint'
  isDragging: boolean
}

export default function ManualRouteEditor({ 
  selectedEdge, 
  onRouteUpdate, 
  onClose 
}: ManualRouteEditorProps) {
  const { getNode, screenToFlowPosition } = useReactFlow()
  const [routePoints, setRoutePoints] = useState<RoutePointHandle[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const dragRef = useRef<RoutePointHandle | null>(null)

  // Initialize route points when edge is selected
  useEffect(() => {
    if (!selectedEdge) {
      setRoutePoints([])
      return
    }

    // Parse existing path points from edge data
    const existingPoints = (selectedEdge.data as any)?.pathPoints || []
    
    if (Array.isArray(existingPoints) && existingPoints.length > 0) {
      // Convert to handle format
      const handles: RoutePointHandle[] = existingPoints
        .filter((point: any) => point.type === 'waypoint')
        .map((point: any, index: number) => ({
          id: `waypoint-${index}`,
          x: point.x,
          y: point.y,
          type: 'waypoint' as const,
          isDragging: false
        }))
      
      setRoutePoints(handles)
    } else {
      // Generate default waypoints for L-shaped route
      const sourceNode = getNode(selectedEdge.source)
      const targetNode = getNode(selectedEdge.target)
      
      if (sourceNode && targetNode) {
        const midY = (sourceNode.position.y + targetNode.position.y) / 2
        const handles: RoutePointHandle[] = [
          {
            id: 'waypoint-0',
            x: sourceNode.position.x,
            y: midY,
            type: 'waypoint',
            isDragging: false
          },
          {
            id: 'waypoint-1', 
            x: targetNode.position.x,
            y: midY,
            type: 'waypoint',
            isDragging: false
          }
        ]
        setRoutePoints(handles)
      }
    }
  }, [selectedEdge, getNode])

  // Handle mouse down on waypoint
  const handleWaypointMouseDown = useCallback((event: React.MouseEvent, point: RoutePointHandle) => {
    event.stopPropagation()
    setIsDragging(true)
    dragRef.current = point
    
    const rect = event.currentTarget.getBoundingClientRect()
    setDragOffset({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    })
  }, [])

  // Handle mouse move for dragging
  const handleMouseMove = useCallback((event: React.MouseEvent) => {
    if (!isDragging || !dragRef.current) return

    const { x, y } = screenToFlowPosition({
      x: event.clientX - dragOffset.x,
      y: event.clientY - dragOffset.y
    })

    setRoutePoints(prev => prev.map(point => 
      point.id === dragRef.current?.id 
        ? { ...point, x, y }
        : point
    ))
  }, [isDragging, dragOffset, screenToFlowPosition])

  // Handle mouse up to end dragging
  const handleMouseUp = useCallback(() => {
    if (isDragging && dragRef.current) {
      setIsDragging(false)
      dragRef.current = null
    }
  }, [isDragging])

  // Add new waypoint
  const addWaypoint = useCallback((event?: React.MouseEvent) => {
    if (!selectedEdge) return

    let x: number, y: number;
    
    if (event) {
      // Use event coordinates
      const coords = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY
      });
      x = coords.x;
      y = coords.y;
    } else {
      // Use center of screen as fallback
      x = window.innerWidth / 2;
      y = window.innerHeight / 2;
    }

    const newPoint: RoutePointHandle = {
      id: `waypoint-${Date.now()}`,
      x,
      y,
      type: 'waypoint',
      isDragging: false
    }

    setRoutePoints(prev => [...prev, newPoint])
  }, [selectedEdge, screenToFlowPosition])

  // Remove waypoint
  const removeWaypoint = useCallback((pointId: string) => {
    setRoutePoints(prev => prev.filter(point => point.id !== pointId))
  }, [])

  // Save route
  const saveRoute = useCallback(() => {
    if (!selectedEdge) return

    // Parse connection ID from different edge formats
    let connectionId: number;
    if (selectedEdge.id.startsWith('connection-')) {
      connectionId = parseInt(selectedEdge.id.replace('connection-', ''));
    } else if (selectedEdge.id.startsWith('device-terminal-')) {
      connectionId = parseInt(selectedEdge.id.replace('device-terminal-', ''));
    } else if (selectedEdge.id.startsWith('auto-terminal-')) {
      connectionId = parseInt(selectedEdge.id.replace('auto-terminal-', ''));
    } else {
      // Try to parse as is
      connectionId = parseInt(selectedEdge.id);
    }
    
    if (isNaN(connectionId)) {
      console.error('Failed to parse connection ID from edge:', selectedEdge.id);
      return;
    }

    const sourceNode = getNode(selectedEdge.source)
    const targetNode = getNode(selectedEdge.target)

    if (!sourceNode || !targetNode) return

    // Convert to RoutePoint format
    const pathPoints: RoutePoint[] = [
      { x: sourceNode.position.x, y: sourceNode.position.y, type: 'start' },
      ...routePoints.map(point => ({
        x: point.x,
        y: point.y,
        type: 'waypoint' as const
      })),
      { x: targetNode.position.x, y: targetNode.position.y, type: 'end' }
    ]


    onRouteUpdate(connectionId, pathPoints, true)
    onClose()
  }, [selectedEdge, routePoints, getNode, onRouteUpdate, onClose])

  // Reset to automatic routing
  const resetToAutomatic = useCallback(() => {
    if (!selectedEdge) return

    // Parse connection ID from different edge formats
    let connectionId: number;
    if (selectedEdge.id.startsWith('connection-')) {
      connectionId = parseInt(selectedEdge.id.replace('connection-', ''));
    } else if (selectedEdge.id.startsWith('device-terminal-')) {
      connectionId = parseInt(selectedEdge.id.replace('device-terminal-', ''));
    } else if (selectedEdge.id.startsWith('auto-terminal-')) {
      connectionId = parseInt(selectedEdge.id.replace('auto-terminal-', ''));
    } else {
      // Try to parse as is
      connectionId = parseInt(selectedEdge.id);
    }
    
    if (isNaN(connectionId)) {
      console.error('Failed to parse connection ID from edge:', selectedEdge.id);
      return;
    }


    onRouteUpdate(connectionId, [], false)
    onClose()
  }, [selectedEdge, onRouteUpdate, onClose])

  if (!selectedEdge) return null

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Редактирование маршрута соединения</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">
            Перетаскивайте точки для изменения маршрута. Кликните на пустое место для добавления новой точки.
          </p>
          
          <div className="flex gap-2 mb-4">
            <button
              onClick={addWaypoint}
              className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
            >
              + Добавить точку
            </button>
            <button
              onClick={resetToAutomatic}
              className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
            >
              Автоматический маршрут
            </button>
          </div>
        </div>

        {/* Route points list */}
        <div className="mb-4">
          <h4 className="font-medium mb-2">Точки маршрута:</h4>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {routePoints.map((point, index) => (
              <div key={point.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <span className="text-sm font-mono">
                  {index + 1}: ({Math.round(point.x)}, {Math.round(point.y)})
                </span>
                <button
                  onClick={() => removeWaypoint(point.id)}
                  className="ml-auto text-red-500 hover:text-red-700 text-sm"
                >
                  Удалить
                </button>
              </div>
            ))}
            {routePoints.length === 0 && (
              <p className="text-gray-500 text-sm">Нет промежуточных точек</p>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Отмена
          </button>
          <button
            onClick={saveRoute}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Сохранить маршрут
          </button>
        </div>
      </div>

      {/* Overlay for mouse events - only for dragging */}
      <div
        className="absolute inset-0 pointer-events-none"
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      />
      
      {/* Clickable overlay for adding waypoints */}
      <div
        className="absolute inset-0 pointer-events-auto"
        onClick={(e) => {
          // Only add waypoint if clicking on the overlay, not on the modal content
          if (e.target === e.currentTarget) {
            addWaypoint(e);
          }
        }}
        style={{ pointerEvents: 'auto' }}
      />

      {/* Waypoint handles */}
      {routePoints.map((point) => (
        <div
          key={point.id}
          className="absolute w-4 h-4 bg-blue-500 border-2 border-white rounded-full cursor-move transform -translate-x-2 -translate-y-2 z-10"
          style={{
            left: point.x,
            top: point.y,
            pointerEvents: 'auto'
          }}
          onMouseDown={(e) => handleWaypointMouseDown(e, point)}
        />
      ))}
    </div>
  )
}
